import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Divider,
    Step,
    StepLabel,
    Stepper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import DocCardImage from "../DocCardImage/DocCardImage";

const getMachineOptionLabel = (option) => {
    if (!option) return "";
    if (typeof option === "string") return option;
    return option.label || option.machine_label || "";
};

const getFileExtension = (filename) => {
    if (!filename) return "";
    const parts = filename.split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const AdminMachineReassignModal = ({
    open,
    onClose,
    documents = [],
    currentCategoryLabel = "",
    machineOptions = [],
    machineLoading = false,
    selectedMachine = null,
    onMachineChange,
    onMachineSearchChange,
    onSubmit,
    submitting = false,
    errorMessage = "",
}) => {
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0);
    const [localErrors, setLocalErrors] = useState([]);
    const hasSelectedDocuments = documents.length > 0;
    const hasSelectedMachine = Number.isFinite(Number(selectedMachine?.machine_id)) && Number(selectedMachine?.machine_id) > 0;
    const targetMachineLabel = getMachineOptionLabel(selectedMachine);
    const categoryLabel = currentCategoryLabel || t("UNCATEGORIZED", { defaultValue: "Uncategorized" });
    const steps = useMemo(
        () => [
            t("GENERAL.SELECTED_DOCUMENTS", { defaultValue: "Selected Documents" }),
            t("GENERAL.MACHINE_MAPPING", { defaultValue: "Machine Mapping" }),
        ],
        [t]
    );

    useEffect(() => {
        if (!open) return;
        setActiveStep(0);
        setLocalErrors([]);
    }, [open, documents.length]);

    const validateStepOne = () => {
        const messages = [];
        if (!hasSelectedDocuments) {
            messages.push(t("MESSAGES.SELECT_DOCUMENTS_TO_CHANGE_MACHINE", { defaultValue: "Please select documents to change machine" }));
        }

        setLocalErrors(messages);
        return messages.length === 0;
    };

    const validateStepTwo = () => {
        const messages = [];
        if (!hasSelectedMachine) {
            messages.push(t("MESSAGES.SELECT_TARGET_MACHINE", { defaultValue: "Select a target machine to continue." }));
        }

        setLocalErrors(messages);
        return messages.length === 0;
    };

    const handleClose = () => {
        if (submitting) return;
        setActiveStep(0);
        setLocalErrors([]);
        onClose();
    };

    const handlePrimaryAction = () => {
        if (activeStep === 0) {
            if (validateStepOne()) {
                setActiveStep(1);
            }
            return;
        }

        if (!validateStepOne()) {
            setActiveStep(0);
            return;
        }
        if (!validateStepTwo()) return;

        setLocalErrors([]);
        onSubmit();
    };

    return (
        <ModalWrapper
            open={open}
            setOpen={() => {}}
            onClose={handleClose}
            width="60vw"
            title={t("BUTTON.CHANGE_MACHINE", { defaultValue: "Change Machine" })}
            subtitle={t("MESSAGES.ADMIN_REMAP_MACHINE_MODAL_SUBTITLE", {
                defaultValue: "Move the selected documentation rows to the correct machine.",
            })}
            actions={
                <Box sx={{ display: "flex", justifyContent: activeStep > 0 ? "space-between" : "flex-end", width: "100%", alignItems: "center" }}>
                    {activeStep > 0 ? (
                        <Button onClick={() => setActiveStep((step) => Math.max(step - 1, 0))} color="secondary" disabled={submitting}>
                            {t("BUTTON.BACK", { defaultValue: "Back" })}
                        </Button>
                    ) : null}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", columnGap: 1.5 }}>
                        <Button onClick={handleClose} disabled={submitting}>
                            {t("BUTTON.CANCEL", { defaultValue: "Cancel" })}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handlePrimaryAction}
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                        >
                            {activeStep === steps.length - 1
                                ? submitting
                                    ? t("BUTTON.SAVING", { defaultValue: "Saving..." })
                                    : t("BUTTON.CHANGE_MACHINE", { defaultValue: "Change Machine" })
                                : t("BUTTON.NEXT", { defaultValue: "Next" })}
                        </Button>
                    </Box>
                </Box>
            }
            content={
                <Stack spacing={2}>
                    {localErrors.length > 0 ? (
                        <Alert severity="error">
                            <Box sx={{ fontWeight: 600, mb: 0.75 }}>
                                {t("UI_TEXT.PLEASE_FIX_THE_FOLLOWING", { defaultValue: "Please fix the following:" })}
                            </Box>
                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                {localErrors.map((message, index) => (
                                    <li key={`${message}-${index}`}>{message}</li>
                                ))}
                            </Box>
                        </Alert>
                    ) : null}

                    {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

                    <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 1 }}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {activeStep === 0 ? (
                        <Stack spacing={2}>
                            <Alert severity="info">
                                {t("MESSAGES.REMAP_SELECTED_DOCUMENTS_SUMMARY", {
                                    defaultValue:
                                        "{{count}} selected document(s) will stay in category {{category}} and move to the machine selected in the next step.",
                                    count: documents.length,
                                    category: categoryLabel,
                                })}
                            </Alert>

                            <Box sx={{ maxHeight: 340, overflowY: "auto", pr: 0.5 }}>
                                <Stack spacing={1}>
                                    {documents.map((document, index) => {
                                        const documentName = document.name || document.document_name;
                                        return (
                                            <Box
                                                key={document.id || document.document_id || `${documentName}-${index}`}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    px: 1.5,
                                                    py: 1.25,
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    borderRadius: 1.5,
                                                }}
                                            >
                                                <Box sx={{ flexShrink: 0 }}>
                                                    <DocCardImage
                                                        filename={documentName}
                                                        path={document.attachment || document.blob_path}
                                                        fileExtension={getFileExtension(documentName)}
                                                    />
                                                </Box>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                        sx={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {documentName || t("LABELS.UNTITLED_DOCUMENT", { defaultValue: "Untitled document" })}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {t("LABELS.CATEGORY", { defaultValue: "Category" })}: {categoryLabel}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        </Stack>
                    ) : null}

                    {activeStep === 1 ? (
                        <Stack spacing={2}>
                            <Autocomplete
                                disablePortal
                                options={machineOptions}
                                value={selectedMachine}
                                loading={machineLoading}
                                onChange={(_, value) => {
                                    onMachineChange(value);
                                    setLocalErrors([]);
                                }}
                                onInputChange={(_, value, reason) => {
                                    if (reason !== "reset") onMachineSearchChange(value);
                                }}
                                isOptionEqualToValue={(option, value) => Number(option?.machine_id) === Number(value?.machine_id)}
                                getOptionLabel={getMachineOptionLabel}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("LABELS.TARGET_MACHINE", { defaultValue: "Target Machine" })}
                                        placeholder={t("MESSAGES.SEARCH_MACHINE_BY_NAME_OR_NUMBER", {
                                            defaultValue: "Search by machine number or name",
                                        })}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {machineLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />

                            <Alert severity="info">
                                {targetMachineLabel
                                    ? t("MESSAGES.TARGET_MACHINE_PREVIEW", {
                                        defaultValue: "Target machine: {{machine}}",
                                        machine: targetMachineLabel,
                                    })
                                    : t("MESSAGES.SELECT_TARGET_MACHINE", {
                                        defaultValue: "Select a target machine to continue.",
                                    })}
                            </Alert>

                            <Divider />

                            <Typography variant="body2" color="text.secondary">
                                {t("MESSAGES.CHANGE_MACHINE_CONFIRMATION_SUMMARY", {
                                    defaultValue: "{{count}} document(s) will move to the selected machine.",
                                    count: documents.length,
                                })}
                            </Typography>
                        </Stack>
                    ) : null}
                </Stack>
            }
        />
    );
};

export default AdminMachineReassignModal;
